<script setup>
/**
 * Finding one piece of wording in a library of hundreds.
 *
 * ── WHY EVERY DROPDOWN IS BUILT FROM THE LIBRARY ────────────────────────────────────
 *
 * Not from a constant. A fixed option list is a claim about Meta's vocabulary, and that claim
 * expires — Meta's own two reference pages disagree about the status enum today. Built from the
 * rows, the Status list never offers "Paused" to an account with nothing paused, and never hides
 * a status Meta invented last week. The counts beside each option come from the whole library,
 * not the filtered view, so they do not change as she narrows.
 *
 * ── THE ATTENTION FILTER IS FIRST BECAUSE IT IS THE ANSWER ──────────────────────────
 *
 * The question an owner opens this screen with is almost never "show me the marketing ones". It
 * is "is anything broken". Refused, paused, disabled, archived — and, crucially, approved but
 * rated medium or low, which is the only warning that arrives before WhatsApp pauses a template
 * for three hours, then six, then disables it for good. One control answers that, and it carries
 * its own count so she can see the answer without pressing it.
 */
import { computed } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import WasloInput from './WasloInput.vue';
import WasloSelect from './WasloSelect.vue';
import { UNTOLD } from '../helpers/template-filter.js';
import { KNOWN_CATEGORIES, KNOWN_STATUSES } from '../helpers/template-facts.js';
import { useWasloI18n } from '../i18n.js';

const props = defineProps({
  /** `{ search, status, category, language, quality, attention }`. */
  modelValue: { type: Object, required: true },
  /** `facets(templates)` from `template-filter.js` — counts over the WHOLE library. */
  facets: { type: Object, required: true },
  /** How many rows the current filter leaves. */
  showing: { type: Number, default: 0 },
  /** Whether anything is narrowed, so "clear" appears only when it would do something. */
  narrowed: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'clear']);

const { t } = useWasloI18n();

const set = patch =>
  emit('update:modelValue', { ...props.modelValue, ...patch });

/** `{ VALUE: count }` → options, with the count in the label and the total as the first entry. */
const options = (counts, label, anyLabel) => {
  const entries = Object.entries(counts ?? {}).sort((left, right) =>
    left[0].localeCompare(right[0])
  );
  return [
    { value: '', label: anyLabel },
    ...entries.map(([value, count]) => ({
      value: value,
      label: `${label(value)} (${count})`,
    })),
  ];
};

/**
 * Meta's word if we have a sentence for it, and META'S OWN WORD if we do not — never folded into
 * the nearest one we recognise. Meta's two reference pages disagree about the status enum today,
 * so an option list that could only render values we know would hide a real row.
 */
const statusLabel = value => {
  if (value === UNTOLD) return t('WASLO.TEMPLATES.STATUS.UNTOLD');
  if (!KNOWN_STATUSES.includes(value)) return value;
  return t(`WASLO.TEMPLATES.STATUS.${value}`);
};

const categoryLabel = value => {
  if (value === UNTOLD) return t('WASLO.TEMPLATES.ROW.CATEGORY_UNTOLD');
  if (!KNOWN_CATEGORIES.includes(value)) return value;
  return t(`WASLO.TEMPLATES.CATEGORY.${value}`);
};

const RATINGS = ['HIGH', 'MEDIUM', 'LOW', 'UNKNOWN'];

const qualityLabel = value => {
  if (value === UNTOLD) return t('WASLO.TEMPLATES.QUALITY.NONE');
  if (!RATINGS.includes(value)) return value;
  return t(`WASLO.TEMPLATES.QUALITY.${value}`);
};

const statusOptions = computed(() =>
  options(
    props.facets.status,
    statusLabel,
    t('WASLO.TEMPLATES.FILTER.ANY_STATUS')
  )
);
const categoryOptions = computed(() =>
  options(
    props.facets.category,
    categoryLabel,
    t('WASLO.TEMPLATES.FILTER.ANY_CATEGORY')
  )
);
const languageOptions = computed(() =>
  options(
    props.facets.language,
    value => value,
    t('WASLO.TEMPLATES.FILTER.ANY_LANGUAGE')
  )
);
const qualityOptions = computed(() =>
  options(
    props.facets.quality,
    qualityLabel,
    t('WASLO.TEMPLATES.FILTER.ANY_QUALITY')
  )
);
</script>

<template>
  <div class="flex flex-col gap-3" data-waslo-templates-filter>
    <WasloInput
      :model-value="modelValue.search"
      :placeholder="t('WASLO.TEMPLATES.FILTER.SEARCH_PLACEHOLDER')"
      :label="t('WASLO.TEMPLATES.FILTER.SEARCH_LABEL')"
      :hint="t('WASLO.TEMPLATES.FILTER.SEARCH_HINT')"
      data-waslo-templates-search
      @update:model-value="value => set({ search: value })"
    />

    <!--
      A grid rather than flex-wrap: four controls that must stay column-aligned, collapsing to one
      per row on a narrow surface. README §3 — a bare `flex: 1` beside a sibling that keeps
      `width: 100%` collapses one of them to nothing.
    -->
    <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <WasloSelect
        :model-value="modelValue.status"
        :label="t('WASLO.TEMPLATES.FILTER.STATUS')"
        :options="statusOptions"
        data-waslo-templates-filter-status
        @update:model-value="value => set({ status: value })"
      />
      <WasloSelect
        :model-value="modelValue.category"
        :label="t('WASLO.TEMPLATES.FILTER.CATEGORY')"
        :options="categoryOptions"
        data-waslo-templates-filter-category
        @update:model-value="value => set({ category: value })"
      />
      <WasloSelect
        :model-value="modelValue.language"
        :label="t('WASLO.TEMPLATES.FILTER.LANGUAGE')"
        :options="languageOptions"
        data-waslo-templates-filter-language
        @update:model-value="value => set({ language: value })"
      />
      <WasloSelect
        :model-value="modelValue.quality"
        :label="t('WASLO.TEMPLATES.FILTER.QUALITY')"
        :options="qualityOptions"
        data-waslo-templates-filter-quality
        @update:model-value="value => set({ quality: value })"
      />
    </div>

    <div class="flex flex-wrap items-center gap-x-3 gap-y-2">
      <Button
        v-if="facets.attention"
        :label="
          t('WASLO.TEMPLATES.FILTER.ATTENTION', { count: facets.attention })
        "
        icon="i-lucide-triangle-alert"
        :variant="modelValue.attention ? 'solid' : 'faded'"
        :color="modelValue.attention ? 'ruby' : 'slate'"
        size="sm"
        data-waslo-templates-filter-attention
        @click="set({ attention: !modelValue.attention })"
      />
      <p
        class="mb-0 text-xs leading-5 text-n-slate-10"
        data-waslo-templates-showing
      >
        {{
          t('WASLO.TEMPLATES.FILTER.SHOWING', {
            showing: showing,
            total: facets.total,
          })
        }}
      </p>
      <Button
        v-if="narrowed"
        :label="t('WASLO.TEMPLATES.FILTER.CLEAR')"
        variant="link"
        color="slate"
        size="sm"
        data-waslo-templates-filter-clear
        @click="emit('clear')"
      />
    </div>
  </div>
</template>
